import { useCallback, useEffect, useRef, useState } from 'react';
import { getFeatureLotId } from './useLotSelection';
import { MAP_MIN_LOT_ZOOM, PARCEL_FILTER_POINTS_MAX_ZOOM } from '../styles/mapTheme';

const DEFAULT_MAX_LOTS = 80;
const DEFAULT_DEBOUNCE_MS = 350;
/** Publica coincidencias ya dibujadas aunque el mapa no haya llegado a idle. */
const SETTLE_MS = 900;
/** Recién ahí un vacío cuenta como definitivo si las teselas no terminan. */
const EMPTY_SETTLE_MS = 1800;

const toText = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
};

const toSurface = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Bucket del tile (`val_b` 0–4). El 0 es válido: no usar `> 0`. */
const toBucket = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
};

const tilesAreReady = (map) => {
  if (!map) return false;
  if (typeof map.areTilesLoaded === 'function') return map.areTilesLoaded();
  return true;
};

/**
 * A zoom bajo mandan los puntos; a zoom de parcela, los polígonos.
 * Evita recorrer las dos familias a la vez en un viewport nacional.
 */
function getQueryLayerIds(map, getFillIds, getPointIds) {
  const zoom = map.getZoom();
  const points = (getPointIds?.() || []).filter((id) => map.getLayer(id));
  const fills = (getFillIds?.() || []).filter((id) => map.getLayer(id));
  if (zoom < MAP_MIN_LOT_ZOOM) return points;
  if (zoom >= PARCEL_FILTER_POINTS_MAX_ZOOM) return fills;
  return [...fills, ...points];
}

/**
 * Arma un lote de listado a partir de un feature del tile. Sin geometría:
 * el click pide el detalle por id, igual que Registros.
 * @param {object} feature
 * @param {string} lotId
 */
function featureToListLot(feature, lotId) {
  const props = feature?.properties || {};
  return {
    lot_id: lotId,
    lot_name: toText(props.name) || lotId,
    province_name: toText(props.province_name),
    department_name: toText(props.department_name),
    type: toText(props.type),
    urbansurface: toSurface(props.urbansurface) ?? toSurface(props.surface),
    val_b: toBucket(props.val_b),
  };
}

function collectLotsFromFeatures(features, maxLots) {
  const seen = new Set();
  const lots = [];
  let truncated = false;

  for (const feature of features) {
    const lotId = getFeatureLotId(feature);
    if (!lotId || seen.has(lotId)) continue;
    seen.add(lotId);
    if (lots.length >= maxLots) {
      truncated = true;
      break;
    }
    lots.push(featureToListLot(feature, lotId));
  }

  return { lots, truncated };
}

/**
 * Coincidencias filtradas dibujadas en el viewport. No es el universo nacional:
 * `queryRenderedFeatures` solo ve teselas cargadas. Tope + debounce para no
 * recorrer decenas de miles de polígonos en cada pan.
 *
 * El skeleton cubre el debounce y la espera de teselas. Un vacío solo se
 * publica cuando las teselas están listas o se agota EMPTY_SETTLE_MS.
 *
 * @param {object} options
 * @param {React.MutableRefObject} options.mapInstanceRef
 * @param {() => string[]} options.getInteractiveParcelLayerIds
 * @param {() => string[]} options.getParcelPointsLayerIds
 * @param {Array|null} options.matchExpression expresión MapLibre del motor de filtros
 * @param {boolean} [options.enabled]
 * @param {number} [options.maxLots]
 * @param {number} [options.debounceMs]
 * @returns {{ lots: object[], truncated: boolean, isSampleView: boolean, isRefreshing: boolean, hasSettled: boolean }}
 */
export function useFilteredViewportLots({
  mapInstanceRef,
  getInteractiveParcelLayerIds,
  getParcelPointsLayerIds,
  matchExpression,
  enabled = false,
  maxLots = DEFAULT_MAX_LOTS,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}) {
  const [lots, setLots] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [isSampleView, setIsSampleView] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasSettled, setHasSettled] = useState(false);

  const matchRef = useRef(matchExpression);
  matchRef.current = matchExpression;
  const snapshotKeyRef = useRef('');
  const hasSettledRef = useRef(false);
  const emptySettleIdRef = useRef(null);

  const publish = useCallback((nextLots, nextTruncated, sampleView) => {
    const snapshotKey = `${nextLots.map((lot) => lot.lot_id).join('|')}|${nextLots.length}|${nextTruncated ? 1 : 0}|${sampleView ? 1 : 0}`;
    if (snapshotKeyRef.current !== snapshotKey) {
      snapshotKeyRef.current = snapshotKey;
      setLots(nextLots);
      setTruncated(nextTruncated);
      setIsSampleView(sampleView);
    }
    hasSettledRef.current = true;
    setHasSettled(true);
    setIsRefreshing(false);
    if (nextLots.length > 0 && emptySettleIdRef.current) {
      clearTimeout(emptySettleIdRef.current);
      emptySettleIdRef.current = null;
    }
  }, []);

  const collect = useCallback((options = {}) => {
    const commitEmpty = Boolean(options.commitEmpty);
    const map = mapInstanceRef.current;
    const expr = matchRef.current;
    if (!map || !map.style || !enabled || !expr) {
      snapshotKeyRef.current = '';
      hasSettledRef.current = false;
      setLots([]);
      setTruncated(false);
      setIsSampleView(false);
      setHasSettled(false);
      setIsRefreshing(false);
      return;
    }

    const sampleView = map.getZoom() < MAP_MIN_LOT_ZOOM;
    const ready = tilesAreReady(map);
    const layerIds = getQueryLayerIds(
      map,
      getInteractiveParcelLayerIds,
      getParcelPointsLayerIds,
    );

    if (layerIds.length === 0) {
      if (!ready && !commitEmpty) {
        if (!hasSettledRef.current) setIsRefreshing(true);
        return;
      }
      publish([], false, sampleView);
      return;
    }

    let features = [];
    try {
      features = map.queryRenderedFeatures(undefined, {
        layers: layerIds,
        filter: expr,
      });
    } catch (err) {
      console.error('useFilteredViewportLots: no se pudieron leer las parcelas de la vista', err);
      if (!commitEmpty) {
        if (!hasSettledRef.current) setIsRefreshing(true);
        return;
      }
      snapshotKeyRef.current = '';
      hasSettledRef.current = true;
      setLots([]);
      setTruncated(false);
      setIsSampleView(sampleView);
      setHasSettled(true);
      setIsRefreshing(false);
      return;
    }

    const { lots: nextLots, truncated: nextTruncated } = collectLotsFromFeatures(features, maxLots);

    if (nextLots.length > 0) {
      publish(nextLots, nextTruncated, sampleView);
      return;
    }

    // Vacío intermedio: skeleton solo si todavía no hay una lectura de este ciclo.
    // Un idle con teselas a medio cargar no debe tapar un listado ya publicado.
    if (!ready && !commitEmpty) {
      if (!hasSettledRef.current) setIsRefreshing(true);
      return;
    }

    publish([], false, sampleView);
  }, [
    mapInstanceRef,
    getInteractiveParcelLayerIds,
    getParcelPointsLayerIds,
    enabled,
    maxLots,
    publish,
  ]);

  useEffect(() => {
    if (!enabled) {
      snapshotKeyRef.current = '';
      hasSettledRef.current = false;
      setLots([]);
      setTruncated(false);
      setIsSampleView(false);
      setHasSettled(false);
      setIsRefreshing(false);
      return undefined;
    }

    const map = mapInstanceRef.current;
    if (!map) return undefined;

    let debounceId = null;
    let settleId = null;
    let emptySettleId = null;

    const armSettle = () => {
      clearTimeout(settleId);
      clearTimeout(emptySettleId);
      if (emptySettleIdRef.current) {
        clearTimeout(emptySettleIdRef.current);
        emptySettleIdRef.current = null;
      }
      settleId = setTimeout(() => collect(), SETTLE_MS);
      emptySettleId = setTimeout(() => collect({ commitEmpty: true }), EMPTY_SETTLE_MS);
      emptySettleIdRef.current = emptySettleId;
    };

    const scheduleRefresh = () => {
      hasSettledRef.current = false;
      setHasSettled(false);
      setIsRefreshing(true);
      clearTimeout(debounceId);
      debounceId = setTimeout(collect, debounceMs);
      armSettle();
    };

    const scheduleIdleCollect = () => {
      clearTimeout(debounceId);
      debounceId = setTimeout(collect, debounceMs);
    };

    setIsRefreshing(true);
    collect();
    armSettle();
    map.on('moveend', scheduleRefresh);
    map.on('zoomend', scheduleRefresh);
    map.on('idle', scheduleIdleCollect);

    return () => {
      clearTimeout(debounceId);
      clearTimeout(settleId);
      clearTimeout(emptySettleId);
      emptySettleIdRef.current = null;
      map.off('moveend', scheduleRefresh);
      map.off('zoomend', scheduleRefresh);
      map.off('idle', scheduleIdleCollect);
    };
  }, [mapInstanceRef, enabled, collect, debounceMs, matchExpression]);

  return { lots, truncated, isSampleView, isRefreshing, hasSettled };
}
